interface SegmentedControlOption<T extends string> {
    value: T;
    label: string;
    /** Disables only this option, e.g. a mode that is locked until an action unlocks it */
    disabled?: boolean;
    /** Native tooltip, typically explaining why the option is disabled */
    title?: string;
}

interface SegmentedControlProps<T extends string> {
    options: SegmentedControlOption<T>[];
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    className?: string;
    ariaLabel?: string;
}

/**
 * Compact mutually-exclusive button group acting as a radio group, e.g.
 * "Entity list / Entry screener" mode toggles.
 */
const SegmentedControl = <T extends string>({
    options,
    value,
    onChange,
    disabled = false,
    className = '',
    ariaLabel
}: SegmentedControlProps<T>) => (
    <div
        aria-label={ariaLabel}
        className={`inline-flex rounded-md border border-zinc-700 bg-black/30 p-0.5 ${className}`}
        role="radiogroup"
    >
        {options.map((option) => (
            <button
                aria-checked={value === option.value}
                className={`rounded px-3 py-1 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    value === option.value ? 'bg-cyan-600 text-white' : 'text-zinc-400 hover:text-zinc-200'
                }`}
                disabled={disabled || option.disabled}
                key={option.value}
                role="radio"
                title={option.title}
                type="button"
                onClick={() => onChange(option.value)}
            >
                {option.label}
            </button>
        ))}
    </div>
);

export default SegmentedControl;
